import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle2, Clock, Calendar, Star } from 'lucide-react';
import DiscountBanner from '../components/DiscountBanner';
import ReviewBanner from '../components/ReviewBanner';

const ServicesPage = () => {
  return (
    <div className="services-page">
      {/* Discount Banner - Top */}
      <DiscountBanner size="large" />
      
      {/* Review Banner */}
      <ReviewBanner />
      
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="page-title">Services & Pricing</h1>
          <p className="page-subtitle">Professional in-home cat sitting tailored to your needs</p>
        </div>
      </section>

      {/* Visit Options */}
      <section className="visit-options-section">
        <div className="container">
          <h2 className="section-title">Choose Your Visit Schedule</h2>
          
          <div className="pricing-grid">
            {/* Once Daily Visit */}
            <Card className="pricing-card">
              <CardHeader>
                <div className="pricing-icon-wrapper">
                  <Clock className="pricing-icon" />
                </div>
                <CardTitle>Once Daily Visit</CardTitle>
                <div className="pricing-amount">
                  <span className="price">£12</span>
                  <span className="price-detail">for up to 3 cats</span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="duration-badge">20-30 minutes</div>
                <p className="pricing-description">Perfect for independent cats who are happy with one check-in per day.</p>
                
                <ul className="pricing-features">
                  <li><CheckCircle2 className="feature-check" /> Feeding & fresh water</li>
                  <li><CheckCircle2 className="feature-check" /> Litter tray cleaning</li>
                  <li><CheckCircle2 className="feature-check" /> Playtime or companionship</li>
                  <li><CheckCircle2 className="feature-check" /> Health monitoring</li>
                  <li><CheckCircle2 className="feature-check" /> Medication if needed</li>
                  <li><CheckCircle2 className="feature-check" /> Daily photo update</li>
                  <li><CheckCircle2 className="feature-check" /> Home guardian tasks</li>
                </ul>
              </CardContent>
            </Card>

            {/* Twice Daily Visit - Best Value */}
            <Card className="pricing-card featured-card">
              <div className="most-popular-badge">
                <Star className="star-icon" />
                <span>Most Popular</span>
              </div>
              <CardHeader>
                <div className="pricing-icon-wrapper featured">
                  <Calendar className="pricing-icon" />
                </div>
                <CardTitle>Twice Daily Visits</CardTitle>
                <div className="pricing-amount">
                  <span className="price">£22</span>
                  <span className="price-detail">for up to 3 cats</span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="duration-badge">20-30 minutes per visit</div>
                <p className="pricing-description">Ideal for cats who prefer more frequent attention and interaction throughout the day.</p>
                
                <ul className="pricing-features">
                  <li><CheckCircle2 className="feature-check" /> Morning & evening visits</li>
                  <li><CheckCircle2 className="feature-check" /> All standard care tasks (twice)</li>
                  <li><CheckCircle2 className="feature-check" /> Extended playtime</li>
                  <li><CheckCircle2 className="feature-check" /> Closer health monitoring</li>
                  <li><CheckCircle2 className="feature-check" /> Two photo updates daily</li>
                  <li><CheckCircle2 className="feature-check" /> Home guardian tasks</li>
                  <li><CheckCircle2 className="feature-check" /> Best value for peace of mind</li>
                </ul>
              </CardContent>
            </Card>

            {/* VIP Visit */}
            <Card className="pricing-card">
              <CardHeader>
                <div className="pricing-icon-wrapper">
                  <Star className="pricing-icon" />
                </div>
                <CardTitle>VIP Extended Visit</CardTitle>
                <div className="pricing-amount">
                  <span className="price">£15</span>
                  <span className="price-detail">per visit</span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="duration-badge">45 minutes</div>
                <p className="pricing-description">Perfect for cats who love extra attention, playtime, and companionship.</p>
                
                <ul className="pricing-features">
                  <li><CheckCircle2 className="feature-check" /> Extended 45-minute visit</li>
                  <li><CheckCircle2 className="feature-check" /> All standard care tasks</li>
                  <li><CheckCircle2 className="feature-check" /> Plenty of playtime</li>
                  <li><CheckCircle2 className="feature-check" /> Extra cuddles & attention</li>
                  <li><CheckCircle2 className="feature-check" /> Detailed photo & video updates</li>
                  <li><CheckCircle2 className="feature-check" /> Home guardian tasks</li>
                  <li><CheckCircle2 className="feature-check" /> Perfect for sociable cats</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="additional-services-section">
        <div className="container">
          <h2 className="section-title">Additional Services & Information</h2>
          
          <div className="additional-grid">
            <Card className="additional-card">
              <CardContent className="additional-content">
                <h3>Additional Cats</h3>
                <div className="additional-price">£5 per cat</div>
                <div className="additional-price-sub">per day</div>
                <p>We happily care for multiple cats. Each additional cat beyond 3 is charged at £5 per cat per day.</p>
              </CardContent>
            </Card>

            <Card className="additional-card">
              <CardContent className="additional-content">
                <h3>Vet Visits</h3>
                <div className="additional-price">£10 flat fee</div>
                <p>If your cat needs to visit the vet during your absence, we'll take them for a flat fee of £10 and £10 per hour waiting.</p>
              </CardContent>
            </Card>

            <Card className="additional-card">
              <CardContent className="additional-content">
                <h3>Bank Holidays</h3>
                <div className="additional-price">50% extra</div>
                <p>Bank holiday visits are charged at an additional 50% to the standard rate.</p>
              </CardContent>
            </Card>

            <Card className="additional-card">
              <CardContent className="additional-content">
                <h3>Christmas & New Year</h3>
                <div className="additional-price">Double rate</div>
                <p>Christmas Day, Boxing Day, and New Year's Day visits are charged at double the standard rate.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Special Offer */}
      <section className="special-offer-section">
        <div className="container">
          <Card className="offer-card">
            <CardContent className="offer-content">
              <div className="offer-badge">Special Offer</div>
              <h2>Get 10% Off Your First Booking!</h2>
              <p>Use code <strong>WALSALL10</strong> when you book to receive 10% off your first cat sitting service.</p>
              <p className="offer-note">Valid for new customers on their first booking only.</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Pricing Factors */}
      <section className="pricing-factors-section">
        <div className="container">
          <h2 className="section-title">Personalised Quote</h2>
          <p className="section-intro">Every cat and home is unique. Your final price depends on:</p>
          
          <div className="factors-grid">
            <div className="factor-item">
              <CheckCircle2 className="factor-icon" />
              <div>
                <h4>Number of Visits Per Day</h4>
                <p>Once, twice, or VIP extended visits</p>
              </div>
            </div>

            <div className="factor-item">
              <CheckCircle2 className="factor-icon" />
              <div>
                <h4>Number of Cats</h4>
                <p>Base price covers up to 3 cats</p>
              </div>
            </div>

            <div className="factor-item">
              <CheckCircle2 className="factor-icon" />
              <div>
                <h4>Location</h4>
                <p>Within Walsall and surrounding areas</p>
              </div>
            </div>

            <div className="factor-item">
              <CheckCircle2 className="factor-icon" />
              <div>
                <h4>Special Care Needs</h4>
                <p>Medication, special diets, or extra attention</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta-section">
        <div className="container">
          <div className="cta-box">
            <h2>Ready to Book Your Cat Sitting Service?</h2>
            <p>Contact us today for a personalised quote and free meet & greet.</p>
            <Link to="/contact">
              <Button size="lg" className="cta-button-large">
                Get Your Personalised Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
